import { BonusSettlementStatus, BonusSettlementSupplierStatus } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';
import { createReducer } from '@reduxjs/toolkit';

import {
    BonusSettlementSupplier,
    BonusSettlementSupplierOverviewDetail,
} from '../../models/bonus-settlement-supplier-overview';
import {
    addSupplier,
    completeSuppliers,
    deleteSupplier,
    fetchBonusSettlementsSupplierOverview,
    fetchBonusSettlementsSupplierOverviewDetails,
    inviteSupplier,
    reclaimSupplier,
    remindSuppliers,
    reopenSupplier,
    resetBonusSettlementsSupplierOverviewState,
    revertReclaimSupplier,
} from './bonus-settlements-supplier-overview-actions';
import { reopenCustomer } from '../../customer-overview/store/bonus-settlements-customer-overview-actions';

export interface BonusSettlementsSupplierOverviewReducerState {
    bonusSettlementsSupplierOverview: PaginatedResponse<BonusSettlementSupplier>;
    details?: BonusSettlementSupplierOverviewDetail;
    requesting: boolean;
}

export const initialBonusSettlementsSupplierOverviewReducerState: BonusSettlementsSupplierOverviewReducerState = {
    bonusSettlementsSupplierOverview: { items: [], total: 0, limit: 10, page: 0 },
    details: undefined,
    requesting: false,
};

export const bonusSettlementsSupplierOverviewReducer = createReducer(
    initialBonusSettlementsSupplierOverviewReducerState,

    builder =>
        builder
            .addCase(fetchBonusSettlementsSupplierOverview.pending, state => {
                state.requesting = true;
            })
            .addCase(fetchBonusSettlementsSupplierOverview.fulfilled, (state, action) => {
                state.bonusSettlementsSupplierOverview = action.payload;
                state.requesting = false;
            })
            .addCase(fetchBonusSettlementsSupplierOverview.rejected, state => {
                state.requesting = false;
            })
            .addCase(fetchBonusSettlementsSupplierOverviewDetails.fulfilled, (state, action) => {
                state.details = action.payload;
            })
            .addCase(resetBonusSettlementsSupplierOverviewState.fulfilled, state => {
                state = initialBonusSettlementsSupplierOverviewReducerState;
            })
            .addCase(deleteSupplier.fulfilled, (state, action) => {
                state.bonusSettlementsSupplierOverview.items = state.bonusSettlementsSupplierOverview.items.filter(
                    supplier => supplier.id !== action.payload
                );
            })
            .addCase(addSupplier.fulfilled, (state, action) => {
                const tempItems = state.bonusSettlementsSupplierOverview.items;
                tempItems.unshift(action.payload);

                state.bonusSettlementsSupplierOverview.items = tempItems;
            })
            .addCase(remindSuppliers.fulfilled, (state, action) => {
                const remindedItems = state.bonusSettlementsSupplierOverview.items.map(item => {
                    if (
                        action.payload.remindAll ||
                        (action.payload.bonusSettlementSupplierIds ?? []).includes(item.id)
                    ) {
                        return { ...item, lastReminderAt: new Date() };
                    }
                    return item;
                });

                state.bonusSettlementsSupplierOverview.items = remindedItems;
            })
            .addCase(completeSuppliers.fulfilled, (state, action) => {
                const completedItems = state.bonusSettlementsSupplierOverview.items.map(item => {
                    if (
                        (item.status === BonusSettlementSupplierStatus.Sent && action.payload.completeAll) ||
                        (action.payload.bonusSettlementSupplierIds ?? []).includes(item.id)
                    ) {
                        return { ...item, status: BonusSettlementSupplierStatus.Completed };
                    }
                    return item;
                });

                state.bonusSettlementsSupplierOverview.items = completedItems;
            })
            .addCase(inviteSupplier.fulfilled, state => {
                if (!state.details) {
                    return;
                }
                state.details.status = BonusSettlementStatus.Sent;
            })
            .addCase(reclaimSupplier.fulfilled, (state, action) => {
                const tempItems = state.bonusSettlementsSupplierOverview.items.map(item => {
                    if (action.payload === item.id) {
                        return { ...item, isReclaimed: true };
                    }
                    return item;
                });

                state.bonusSettlementsSupplierOverview.items = tempItems;
            })
            .addCase(revertReclaimSupplier.fulfilled, (state, action) => {
                const tempItems = state.bonusSettlementsSupplierOverview.items.map(item => {
                    if (action.payload === item.id) {
                        return { ...item, isReclaimed: false };
                    }
                    return item;
                });

                state.bonusSettlementsSupplierOverview.items = tempItems;
            })
            .addCase(reopenSupplier.fulfilled, (state, action) => {
                const tempItems = state.bonusSettlementsSupplierOverview.items.map(item => {
                    if (action.payload === item.id) {
                        return { ...item, status: BonusSettlementSupplierStatus.Open };
                    }
                    return item;
                });

                state.bonusSettlementsSupplierOverview.items = tempItems;
            })
            .addCase(reopenCustomer.fulfilled, state => {
                if (state?.details) {
                    state.details.status = BonusSettlementStatus.ReadyToComplete;
                }
            })
);
